import type {
  GroupResponsibleState,
  Participant,
  ParticipantFilterType,
  ParticipantService,
} from '@/scopes/account/group-responsible/domain/types'
import { sortParticipants } from './utils'

export const PARTICIPANT_SERVICE_FIELDS: Array<{
  labelKey: string
  getService: (p: Participant) => ParticipantService | undefined
}> = [
  { labelKey: 'common_ski_pass', getService: p => p.skiPass },
  { labelKey: 'common_ski_material', getService: p => p.skiMaterial },
  { labelKey: 'common_ski_lesson', getService: p => p.skiLesson },
  { labelKey: 'groups_responsible_sheets', getService: p => p.sheets },
  { labelKey: 'groups_responsible_towels', getService: p => p.towels },
  { labelKey: 'groups_responsible_travel_insurance', getService: p => p.travelInsurance },
  { labelKey: 'groups_responsible_cancel_insurance', getService: p => p.cancelInsurance },
  { labelKey: 'groups_responsible_policy_costs', getService: p => p.policyCosts },
  { labelKey: 'groups_responsible_size_of_sweater', getService: p => p.sweaterSize },
]

export function getHasSheets(state: GroupResponsibleState): boolean {
  return Object.values(state.participants).some(p => p.sheets !== undefined)
}

export function getHasTowels(state: GroupResponsibleState): boolean {
  return Object.values(state.participants).some(p => p.towels !== undefined)
}

export function getHasEarlyDeparture(state: GroupResponsibleState): boolean {
  return Object.values(state.participants).some(p => p.earlyDeparture !== undefined)
}

function getActiveParticipant(state: GroupResponsibleState) {
  return state.activeExtraInfoId ? state.participants[state.activeExtraInfoId] : null
}

export function getParticipantExtraInfo(state: GroupResponsibleState) {
  const activeParticipant = getActiveParticipant(state)
  let roomData

  if (state.rooms && activeParticipant?.bookingCustomerId) {
    roomData = Object.values(state.rooms).find((room) => {
      return room.participantsInfo.some(p => p.bookingCustomerId === activeParticipant.bookingCustomerId)
    })
  }

  return {
    name: activeParticipant?.name || '',
    bookingNumber: activeParticipant?.bookingNumber,
    email: activeParticipant?.email || '-',
    hasRoomAssigned: activeParticipant?.hasRoomAssigned,
    mobileNumber: activeParticipant?.mobileNumber,
    telephoneNumber: activeParticipant?.telephoneNumber,
    accommodationName: roomData?.accommodationName || '-',
    roomType: roomData?.type || '-',
    skiPass: activeParticipant?.skiPass,
    skiMaterial: activeParticipant?.skiMaterial,
    skiLesson: activeParticipant?.skiLesson,
    sheets: activeParticipant?.sheets,
    towels: activeParticipant?.towels,
    travelInsurance: activeParticipant?.travelInsurance,
    cancelInsurance: activeParticipant?.cancelInsurance,
    policyCosts: activeParticipant?.policyCosts,
    sweaterSize: activeParticipant?.sweaterSize,
  }
}

export function getParticipantExtrasTotal(state: GroupResponsibleState): {
  total: number
  paidServicesLabelKeys: string[]
} {
  const activeParticipant = getActiveParticipant(state)
  if (!activeParticipant)
    return { total: 0, paidServicesLabelKeys: [] }

  const paidServices = PARTICIPANT_SERVICE_FIELDS.filter(({ getService }) => {
    const service = getService(activeParticipant)
    return service !== undefined && service.price > 0
  })

  const total = paidServices.reduce((sum, { getService }) => sum + (getService(activeParticipant)?.price ?? 0), 0)

  return {
    total,
    paidServicesLabelKeys: paidServices.map(({ labelKey }) => labelKey),
  }
}

export function getParticipantCounts(state: GroupResponsibleState): {
  all: number
  notPaid: number
  partialPaid: number
  paid: number
} {
  const participants = Object.values(state.participants)

  const all = participants.length
  const notPaid = participants.filter(p => p.paymentStatus === 'not_paid').length
  const partialPaid = participants.filter(p => p.paymentStatus === 'partial_paid').length
  const paid = participants.filter(p => p.paymentStatus === 'paid').length

  return { all, notPaid, partialPaid, paid }
}

export function getFilteredParticipants(state: GroupResponsibleState, filter: ParticipantFilterType, searchTerm = '') {
  const trimmedSearchTerm = searchTerm.trim().toLowerCase()
  return Object.values(state.participants)
    .filter((participant) => {
      const matchesStatus = filter === 'all' || participant.paymentStatus === filter
      const matchesSearch = trimmedSearchTerm === '' || participant.name?.toLowerCase().includes(trimmedSearchTerm)
      return matchesStatus && matchesSearch
    })
    .sort(sortParticipants)
}
